<template>
    <v-content>
        <v-toolbar class="content-toolbar">
            <font-awesome-icon :icon="['fal', 'file-alt']" size="2x"/>
            <v-toolbar-title>Content
                <template v-if="!areAllContentTypesShowing"> > {{selectedContentName}}</template>
            </v-toolbar-title>
            <div class="search-input-container">
                <font-awesome-icon :icon="['fal', 'search']" class="search-input"/>
                <v-text-field color="accent" placeholder="Type to search for entries" hide-details
                              v-model="searchFilterTerm"></v-text-field>
            </div>
            <div class="reference-filter-container" v-if="doesSelectedContentTypeHaveReferences">
                <div class="content">
                    <div class="content-2">
                        <reference-filter></reference-filter>
                    </div>
                </div>
            </div>
            <div class="author-filter-container">
                <div class="content">
                    <div class="content-2">
                        <v-select clearable label="Author" :items="authors" color="accent"
                                  v-model="authorFilter" :menu-props="vSelectMenuProps"></v-select>
                    </div>
                </div>
            </div>
            <div class="content-2">
            </div>
            <v-spacer></v-spacer>
            <div v-if="isSelectedContentTypeOrdered && selectedRestrictedContentType">
                <v-tooltip bottom>
                    <v-btn color="info" slot="activator"
                           :to="{ name: 'reorderContent', params: { contentTypeIdentifier: selectedRestrictedContentType.identifier }}">
                        <font-awesome-icon :icon="['fal', 'sort']"/>
                    </v-btn>
                    <span>Change {{selectedContentName}} order</span>
                </v-tooltip>
            </div>
            <div class="add-entry-dropdown-container">
                <template v-if="selectedRestrictedContentType">
                    <v-btn color="info"
                           :to="{ name: 'spaceDashboardAddEntry', params: { contentTypeIdentifier: selectedRestrictedContentType.identifier }}">
                        <span class="settings-icon-spacer capitalize-text">Add {{selectedRestrictedContentType.name}}</span>
                    </v-btn>
                </template>
                <v-menu offset-y>
                    <v-btn slot="activator" color="info">
                        <template v-if="!selectedRestrictedContentType">
                            <span class="settings-icon-spacer capitalize-text">Add Entry</span>
                            <span class="icon-spacer"></span>
                        </template>
                        <font-awesome-icon :icon="['fal', 'chevron-down']"/>
                    </v-btn>
                    <v-list v-if="spaceRestrictedContentTypes">
                        <v-list-tile v-for="contentType in spaceRestrictedContentTypes"
                                     v-bind:data="contentType"
                                     v-bind:key="contentType.identifier"
                                     :to="{ name: 'spaceDashboardAddEntry', params: { contentTypeIdentifier: contentType.identifier }}">
                            <v-list-tile-title>
                                <span class="capitalize-text">{{contentType.name}}</span>
                            </v-list-tile-title>
                        </v-list-tile>
                    </v-list>
                </v-menu>
            </div>

        </v-toolbar>
        <div class="content-container">
            <div class="dashboard-sidebar-container">
                <v-navigation-drawer permanent>
                    <v-subheader>Filters</v-subheader>
                    <template v-if="spaceContentTypesArray">
                        <v-list>
                            <template v-for="(contentType, index) in spaceContentTypesArray">
                                <v-divider></v-divider>
                                <v-list-tile v-on:click="setContentTypeState(contentType.identifier)"
                                             v-bind:class="{'selected-filter': contentType.identifier === selectedContentTypeIdentifier}">
                                    <v-list-tile-title>{{contentType.name}}</v-list-tile-title>
                                </v-list-tile>
                                <v-divider v-if="index === 0" class="all-filter-divider"></v-divider>
                            </template>
                        </v-list>
                        <v-divider></v-divider>
                        <v-container fluid>
                            <v-checkbox v-model="showArchived" hide-details>
                                <div slot="label" class="filter-checkbox-label">
                                    Show archived entries
                                </div>
                            </v-checkbox>
                        </v-container>
                    </template>
                    <v-progress-linear v-else :indeterminate="true" color="secondary"></v-progress-linear>
                </v-navigation-drawer>
            </div>
            <v-container fill-height>
                <v-layout>
                    <v-flex>
                        <v-data-table
                                :headers="headers"
                                :items="mappedEntries"
                                :pagination.sync="storePaginationSync"
                                :loading="isDashboardLoading"
                                class="elevation-1 interactive-data-table hidden-rows-per-page-selection"
                        >
                            <v-progress-linear slot="progress" :indeterminate="true" color="accent"></v-progress-linear>
                            <template slot="items" slot-scope="props">
                                <tr v-on:click="navigateEdit(props.item)" slot="activator"
                                    v-on:mouseenter="setHoverIndex(props.index)">
                                    <td>{{ props.item.id}}</td>
                                    <td>
                                        <span v-if="hoverIndex !== props.index">{{ props.item.name}}</span>
                                        <sfr-entry-tooltip v-else :entry="props.item.originalEntry">
                                            <span slot="activator">{{ props.item.name}}</span>
                                        </sfr-entry-tooltip>
                                    </td>
                                    <td v-if="areAllContentTypesShowing">{{ props.item.contentType}}</td>
                                    <td>{{ props.item.author}}</td>
                                    <td>{{ props.item.updated | formatDate}}</td>
                                    <td v-bind:class="cssComputeStatusColor(props.item.status)">{{
                                        props.item.status}}
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-flex>
                </v-layout>
            </v-container>
        </div>

    </v-content>
</template>

<script>
    import BaseLoadingComponent from './BaseLoading'
    import ReferenceFilter from './views/ReferenceFilter'
    import moment from '../services/moment'
    import lodash from '../services/lodash'
    import helpers from '../services/helpers'

    import {
        LOADING_PRISTINE,
        LOADING_STARTED,
        SET_CONTENT_DASHBOARD_STATE,
        SET_CONTENT_DASHBOARD_STATE_AND_RELOAD,
        SET_CONTENT_DASHBOARD_DATA_TABLE_PAGINATION_STATE,
        GET_CONTENT_DASHBOARD_DATA_TABLE_PAGINATION_STATE,
        GET_FILTERED_ENTRIES,
        SUPER_ADMIN_ROLE,
    } from "../services/constants"

    export default {
        name: 'space-dashboard-content',
        extends: BaseLoadingComponent,
        data: function () {
            return {
                hoverIndex: -1,
                authorFilter: ''
            }
        },
        computed: {
            headers() {
                const arr = [
                    {text: 'ID', value: 'id'},
                    {text: 'Name', value: 'name'},
                    {text: 'Author', value: 'author'},
                    {text: 'Updated', value: 'updated'},
                    {text: 'Status', value: 'status'},
                ]
                if (this.areAllContentTypesShowing) {
                    arr.splice(2, 0, {text: 'Type', value: 'contentType'})
                }
                return arr
            },
            spaceContentTypes() {
                return this.$store.state.spaceContentTypes
            },
            spaceRestrictedContentTypes() {
                return this.spaceContentTypes.filter(contentType => {
                    return !(this.isRestricted
                        && contentType
                        && contentType.role
                        && contentType.role === SUPER_ADMIN_ROLE)
                })
            },
            selectedContentTypeIdentifier: function () {
                return this.$store.state.contentDashboardState.selectedContentType;
            },
            areAllContentTypesShowing() {
                return this.selectedContentTypeIdentifier === 'all'
            },
            searchFilterTerm: {
                get() {
                    return this.$store.state.contentDashboardState.searchFilterTerm
                },
                set(newValue) {
                    this.$store.commit(SET_CONTENT_DASHBOARD_STATE, {
                        searchFilterTerm: newValue
                    })
                }
            },
            showArchived: {
                get() {
                    return this.$store.state.contentDashboardState.showArchived
                },
                set(newValue) {
                    this.$store.dispatch(SET_CONTENT_DASHBOARD_STATE_AND_RELOAD, {
                        showArchived: newValue,
                    });
                }
            },
            selectedContentName() {
                return this.selectedContentType ? this.selectedContentType.name : null
            },
            selectedContentType() {
                if (this.$store.state.spaceContentTypes) {
                    return this.$store.state.spaceContentTypes.find(contentType => {
                        return contentType.identifier === this.$store.state.contentDashboardState.selectedContentType
                    });
                } else {
                    return null
                }
            },
            isSelectedContentTypeOrdered() {
                let isOrdered = false
                if (this.selectedContentType) {
                    this.selectedContentType.fields.forEach(field => {
                        field.validations.forEach(validation => {
                            if (validation.name === 'system' && validation.value === 'order') {
                                isOrdered = true
                            }
                        })
                    })
                }
                return isOrdered
            },
            doesSelectedContentTypeHaveReferences() {
                if (this.selectedContentType) {
                    return this.selectedContentType.fields.some(helpers.isFieldANonAssetReferenceField)
                }
                return false
            },
            storePaginationSync: {
                get() {
                    return this.$store.getters[GET_CONTENT_DASHBOARD_DATA_TABLE_PAGINATION_STATE]
                },
                set(newValue) {
                    this.$store.commit(SET_CONTENT_DASHBOARD_DATA_TABLE_PAGINATION_STATE, newValue)
                }
            },
            isDashboardLoading() {
                return this.$store.state.contentDashboardState.loadingState === LOADING_STARTED
            },
            selectedRestrictedContentType() {
                if (this.isRestricted
                    && this.selectedContentType
                    && this.selectedContentType.role
                    && this.selectedContentType.role === SUPER_ADMIN_ROLE) {
                    return null
                } else {
                    return this.selectedContentType
                }
            },
            spaceContentTypesArray: function () {
                const arr = this.$store.state.spaceContentTypes.map(contentType => {
                    return lodash.pick(['identifier', 'name'], contentType)
                })
                arr.unshift({
                    identifier: "all",
                    name: "All",
                })
                return arr
            },
            mappedEntries: function () {
                return this.entries.map(entry => {
                    return {
                        id: entry.id,
                        name: this.getPrimaryDisplayName(entry),
                        contentType: entry.contentType.name,
                        contentTypeIdentifier: entry.contentType.identifier,
                        author: `${entry.author.firstName} ${entry.author.lastName}`,
                        updated: entry.updated,
                        status: entry.status.toUpperCase(),
                        originalEntry: entry,
                    }
                })
            },
            entries() {
                let entriesFromStore = this.$store.getters[GET_FILTERED_ENTRIES]
                if (this.authorFilter) {
                    entriesFromStore = entriesFromStore.filter(entry => {
                        return `${entry.author.firstName} ${entry.author.lastName}`.toLowerCase() === this.authorFilter.toLowerCase()
                    })
                }
                return entriesFromStore
            },
            currentUser: function () {
                return this.$store.state.currentUser
            },
            isRestricted() {
                return this.currentUser ? this.currentUser.role !== SUPER_ADMIN_ROLE : true
            },
            authors() {
                return this.entries.map((entry) => {
                    return `${entry.author.firstName} ${entry.author.lastName}`
                })
            },
            vSelectMenuProps() {
                return {
                    contentClass: 'author-filter-global-select-hook'
                }
            },
        },
        methods: {
            setContentTypeState: function (contentTypeIdentifier) {
                this.$store.dispatch(SET_CONTENT_DASHBOARD_STATE_AND_RELOAD, {
                    selectedContentType: contentTypeIdentifier,
                });
            },
            setArchiveFilter: function ($event) {
                this.$store.dispatch(SET_CONTENT_DASHBOARD_STATE_AND_RELOAD, {
                    showArchived: $event,
                });
            },
            setHoverIndex(index) {
                this.hoverIndex = index
            },
            getPrimaryDisplayName: helpers.getPrimaryDisplayName,
            navigateEdit: function (mappedEntry) {
                const isAllowedToNavigate = this.spaceRestrictedContentTypes.find(contentType => {
                    return contentType.identifier === mappedEntry.contentTypeIdentifier
                })
                if (isAllowedToNavigate) {
                    this.$router.push(
                        {
                            name: 'spaceDashboardEditEntry',
                            params: {
                                contentTypeIdentifier: mappedEntry.contentTypeIdentifier,
                                id: mappedEntry.id,
                            }
                        })
                }
            },
            cssComputeStatusColor: helpers.cssComputeStatusColor
        },
        filters: {
            formatDate: function (date) {
                return moment.formatFullDateAndTime(date);
            }
        }
    }
</script>

<style scoped lang="scss">
    @import "../meta";

    .content-toolbar {
        margin-bottom: 4px;
        position: fixed;
        z-index: 4;
    }

    .content-container {
        display: flex;
        margin: 68px 0 0 $sidebar-width;
        height: calc(100% - 68px);
    }

    .dashboard-sidebar-container {
        height: 100%;
        position: fixed;
        left: 0;
    }

    .selected-filter {
        background: #F2F2F2;
    }

    .all-filter-divider {
        margin: 0 0 2em 0;
    }

    .interactive-data-table {
        cursor: pointer;
    }

    .filter-checkbox-label {
        margin: -2px 0 0 0;
    }

    .search-input-container {
        width: 500px;
        display: flex;
        align-items: center;
        padding: 0 1em 0 0;
    }

    .search-input {
        margin: 0 1em 0 5em;
    }

    .author-filter-container, .reference-filter-container {
        height: 100%;

        .content {
            $margin-height: 10px;
            border-left: solid 1px $accent;
            margin: $margin-height 0 0;
            height: calc(100% - #{$margin-height * 2});
        }

        .content-2 {
            position: relative;
            margin: 0 10px;
            bottom: 4px;
        }
    }
</style>
